/**
 * Customer Categories Service
 * Manages discount groups for customers
 */

import api from './api';
import { CustomerCategory } from '../types';

export const customerCategoriesService = {
  /**
   * Get all customer categories
   */
  getAll: async (): Promise<CustomerCategory[]> => {
    const response = await api.get('/customers/categories');
    return response.data?.data || [];
  },

  /**
   * Get a specific category by ID
   */
  getById: async (id: string): Promise<CustomerCategory> => {
    const response = await api.get(`/customers/categories/${id}`);
    return response.data?.data;
  },

  /**
   * Create a new category
   */
  create: async (category: Omit<CustomerCategory, 'id' | 'createdAt' | 'updatedAt' | 'customerCount'>): Promise<CustomerCategory> => {
    const response = await api.post('/customers/categories', category);
    return response.data?.data;
  },

  /**
   * Update a category
   */
  update: async (id: string, updates: Partial<CustomerCategory>): Promise<CustomerCategory> => {
    const response = await api.patch(`/customers/categories/${id}`, updates);
    return response.data?.data;
  },

  /**
   * Delete a category (soft delete)
   */
  delete: async (id: string): Promise<void> => {
    await api.delete(`/customers/categories/${id}`);
  },

  /**
   * Assign category to multiple customers
   */
  bulkAssign: async (categoryId: string, customerIds: string[], applyDefaults: boolean = true): Promise<{ assignedCount: number }> => {
    const response = await api.post('/customers/bulk-assign-category', {
      categoryId,
      customerIds,
      applyDefaults,
    });
    return response.data?.data;
  },

  /**
   * Bulk update commercial terms for multiple customers
   */
  bulkUpdateTerms: async (customerIds: string[], updates: {
    charterDiscount?: number;
    scheduleDiscount?: number;
    creditLimit?: number;
  }): Promise<{ updatedCount: number }> => {
    const response = await api.post('/customers/bulk-update-terms', {
      customerIds,
      updates,
    });
    return response.data?.data;
  },
};

